import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
  Navigate,
} from 'react-router-dom';

import { AuthProvider } from './context/AuthContext';
import { PropertyProvider } from './context/PropertyContext';
import { LanguageProvider } from './context/LanguageContext';

import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';

import HomePage from './pages/HomePage';
import PropertyDetailsPage from './pages/PropertyDetailsPage';
import TokenPurchasePage from './pages/TokenPurchasePage';
import RegisterPage from './pages/RegisterPage';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import AddProperty from './pages/AddProperty';
import EditProperty from './pages/EditProperty';
import MyProperties from './pages/MyProperties';
import FavoritesPage from './pages/FavoritesPage';
import PurchasedContactsPage from './pages/PurchasedContactsPage';
import AuthCallback from './pages/AuthCallback';
import AdminLayout from './admin/AdminLayout';
import AdminDashboardPage from './admin/AdminDashboardPage';
import AdminUsersPage from './admin/AdminUsersPage';
import AdminListingsPage from './admin/AdminListingsPage';
import AdminOrdersPage from './admin/AdminOrdersPage';
import AdminProductsPage from './admin/AdminProductsPage';
import PayRedirect from './pages/PayRedirect';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentFailurePage from './pages/PaymentFailurePage';
import SearchPage from './pages/SearchPage';

// Layout and routing
const PublicLayout: React.FC = () => (
  <div className="flex flex-col min-h-screen">
    <Navbar />
    <main className="flex-grow">
      <Outlet />
    </main>
    <Footer />
  </div>
);

const AppContent: React.FC = () => (
  <Routes>
    <Route element={<AdminLayout />}>
      <Route path="/admin" element={<AdminDashboardPage />} />
      <Route path="/admin/users" element={<AdminUsersPage />} />
      <Route path="/admin/listings" element={<AdminListingsPage />} />
      <Route path="/admin/orders" element={<AdminOrdersPage />} />
      <Route path="/admin/products" element={<AdminProductsPage />} />
    </Route>
    <Route element={<PublicLayout />}>
      <Route path="/" element={<HomePage />} />
      <Route path="/property/:id" element={<PropertyDetailsPage />} />
      <Route path="/tokens" element={<TokenPurchasePage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/profil" element={<ProfilePage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/buy" element={<Navigate to="/search?purpose=SALE" replace />} />
      <Route path="/rent" element={<Navigate to="/search?purpose=RENT" replace />} />
      {/* Removed duplicate /profile route that pointed to HomePage */}
      <Route path="/add-property" element={<AddProperty />} />
      <Route path="/edit-property/:id" element={<EditProperty />} />
      <Route path="/my-properties" element={<MyProperties />} />
      <Route path="/favorites" element={<FavoritesPage />} />
      <Route path="/purchased-contacts" element={<PurchasedContactsPage />} />
      <Route path="/buy-tokens" element={<TokenPurchasePage />} />
      <Route path="/pay-redirect/:orderId" element={<PayRedirect/>}/>
      <Route path="/success" element={<PaymentSuccessPage />} />
      <Route path="/failure" element={<PaymentFailurePage />} />
      <Route path="*" element={<HomePage />} />
    </Route>
  </Routes>
);

// App wrapper with context providers
const App: React.FC = () => (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <PropertyProvider>
            <AppContent />
          </PropertyProvider>
        </AuthProvider>
      </LanguageProvider>
    </Router>
);

export default App;
